import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GetMentionsResponse } from '@omnea-app/comments/commentsFrontendClient'
import { CommentResponse, CommentsHttpClient } from '@omnea-app/comments/commentsHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { requestCommentsQueryKey } from '@omnea-app/fe-requests/useGetRequestComment'
import { entityCommentsQueryKey } from '@omnea-app/frontend/components/shared/data/comments/useListComments'
import { getMentionsQueryKey, mentionsQueryKey } from './useGetMentions'

export type MarkMentionsResolvedInput = {
  mentions: Array<{ mentionId: string; requestId: string | null }>
  isResolved: boolean
}

export type MentionsResponse = CommentResponse<'/mentions/mark-resolved', 'get'>

export function useMarkMentionsResolved() {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    mutationFn: async ({ isResolved, mentions }: MarkMentionsResolvedInput) => {
      const apiClient = new CommentsHttpClient({ authorization: await getToken() })
      const { data, error } = await apiClient.POST('/mentions/mark-resolved', {
        body: { isResolved, mentionIds: mentions.map((m) => m.mentionId) },
      })
      if (error) {
        throw new Error(error.suggestion)
      }
      return data
    },
    onSuccess: async () => {
      // Refetch all comment and mention queries to ensure consistency
      // Covers both resolved and unresolved mentions despite optimistic update
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [requestCommentsQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [entityCommentsQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [mentionsQueryKey] }),
      ])
    },
    onMutate: async ({ isResolved, mentions }: MarkMentionsResolvedInput) => {
      const idsToMove = new Set(mentions.map((m) => m.mentionId))
      const requestIds = new Set(mentions.map((m) => m.requestId).filter((id): id is string => id !== null))

      const updateScope = (sourceKey: string[], targetKey: string[]) => {
        const sourceData = queryClient.getQueryData<GetMentionsResponse>(sourceKey)
        const targetData = queryClient.getQueryData<GetMentionsResponse>(targetKey)
        if (!sourceData) return

        const toMove = sourceData.mentions.filter((m) => idsToMove.has(m.id))
        const remaining = sourceData.mentions.filter((m) => !idsToMove.has(m.id))

        queryClient.setQueryData<GetMentionsResponse>(sourceKey, {
          mentions: remaining,
          totalCount: sourceData.totalCount - toMove.length,
        })

        if (targetData) {
          const moved = toMove.map((m) => ({ ...m, isResolved }))
          const mentions = [...targetData.mentions, ...moved].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
          queryClient.setQueryData<GetMentionsResponse>(targetKey, {
            mentions,
            totalCount: targetData.totalCount + moved.length,
          })
        }
      }

      // Update dashboard queries
      updateScope(getMentionsQueryKey(!isResolved), getMentionsQueryKey(isResolved))

      // Update request-scoped queries
      for (const requestId of requestIds.values()) {
        updateScope(getMentionsQueryKey(!isResolved, requestId), getMentionsQueryKey(isResolved, requestId))
      }
    },
  })
}
